import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { useTable } from "react-table";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import {
  useCancelSubscription,
  useAddPaymentMethod,
  useSetDefaultPaymentMethod,
  useDeletePaymentMethod,
  useReactivateSubscription,
  useBillingData,
  useRecurlySubscriptionStatus,
  usePremiumPlanPrices,
} from "lib/hooks/premiumHooks";
import { useRsoConnection } from "@ugg/shared/api/requests/accounts/oauth-hooks";
import { SvgArrowFillDown } from "@ugitgud/legos/ui/icon/SVG/ArrowFillDown";
import { Dropdown } from "@ugitgud/legos/ui/form/dropdown/Dropdown";
import { DropdownButton } from "@ugitgud/legos/ui/form/dropdown/DropdownButton";
import { DropdownOptions } from "@ugitgud/legos/ui/form/dropdown/DropdownOptions";
import { DropdownOption } from "@ugitgud/legos/ui/form/dropdown/DropdownOption";
import { ReactComponent as IconX } from "svg/x.svg";
import { RecurlyProvider, Elements, useRecurly } from "@recurly/react-recurly";
import { State } from "country-state-city";
import CheckoutForm, { FormState } from "components/Checkout/components/CheckoutForm";
import { useFormValidation } from "components/Checkout/hooks/useFormValidation";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import DeletePaymentMethodModal from "./DeletePaymentMethodModal";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgMiscPaypal } from "@ugitgud/legos/ui/icon/SVG/MiscPaypal";
import { SvgMiscCreditCard } from "@ugitgud/legos/ui/icon/SVG/MiscCreditCard";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import FeedbackForm from "components/Masthead/Feedback/FeedbackForm";

const RECURLY_PUBLIC_KEY = process.env.UGG_PUBLIC_RECURLY_PUBLIC_KEY || "";

interface Invoice {
  totalPaid?: number | null;
  periodStart?: number | null;
  brand?: string | null;
  last4?: string | null;
  currency?: string | null;
}

interface PaymentMethodOption {
  value: string;
  label: string;
  icon: string;
  type: "card" | "paypal";
}

interface BillingMessageData {
  prefix: string;
  date?: string;
  middle?: string;
  amount?: string;
}

const getBillingMessage = (
  hasPaymentMethod: boolean,
  isFreeTrial: boolean,
  renewDate: string | null,
  unitAmount: number | null | undefined,
  isCanceledButActive: boolean,
): BillingMessageData => {
  const formattedAmount = unitAmount ? `$${(unitAmount / 100).toFixed(2)}` : "-";
  const formattedDate = renewDate || "-";

  // Canceled but still active - subscription will end
  if (isCanceledButActive) {
    return {
      prefix: "Your subscription has been canceled and will end on",
      date: formattedDate,
      middle: ".",
    };
  }

  if (!hasPaymentMethod && isFreeTrial) {
    return {
      prefix: "Your free trial ends on",
      date: formattedDate,
      middle: ". Add a payment method to keep your PLUS benefits.",
    };
  }

  if (!hasPaymentMethod) {
    return {
      prefix: "No payment method on file. Please add a payment method to continue your subscription.",
    };
  }

  if (isFreeTrial) {
    return {
      prefix: "Your free trial ends on",
      date: formattedDate,
      middle: ". You'll then be charged",
      amount: formattedAmount,
    };
  }

  return {
    prefix: "Your subscription will automatically renew on",
    date: formattedDate,
    middle: "and you'll be charged",
    amount: formattedAmount,
  };
};

const getPlanTypeLabel = (planInterval: string | null | undefined, hasSubscription: boolean): React.ReactNode => {
  if (!hasSubscription) return <span className="font-medium text-white">No Current Plan</span>;
  if (planInterval === "12") return <span className="font-medium text-accent-yellow-400">PLUS • Annual</span>;
  if (planInterval === "1") return <span className="font-medium text-accent-yellow-400">PLUS • Monthly</span>;
  return <span className="font-medium text-white">No Current Plan</span>;
};

export const BillingOverview: React.FC = () => {
  const { getProfileIconImg } = getRiotAssetsContext();

  // Get summoner data from RSO connection
  const { summoner, loading: userLoading } = useRsoConnection();

  // Fetch billing data from API
  const { data: billingData, loading: billingLoading, refetch: refetchBillingData } = useBillingData();

  // Use Recurly subscription status to determine premium status
  const { isActive: hasActiveSubscription, loading: subscriptionLoading } = useRecurlySubscriptionStatus();

  // Get plan prices to check for free trial availability
  const { data: { pricesByDuration } = {} } = usePremiumPlanPrices();
  const annualPlanHasFreeTrial = pricesByDuration?.[12]?.hasFreeTrial ?? false;

  const subscription = billingData.subscription;
  const invoices = billingData.invoices;
  const unitAmount = subscription?.unitAmount;

  // Determine subscription state
  const isCanceledButActive = hasActiveSubscription && subscription?.cancelAtPeriodEnd === true;
  const isActivePremium = hasActiveSubscription && !subscription?.cancelAtPeriodEnd;

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodOption | null>(null);
  const [paymentMethodError, setPaymentMethodError] = useState(false);

  const { cancelSubscription, isLoading: isCanceling } = useCancelSubscription({
    onCompleted: () => {
      setShowCancelModal(false);
      refetchBillingData();
    },
    onError: (error) => {
      console.error("Failed to cancel subscription:", error);
    },
  });

  const { reactivateSubscription, isLoading: isReactivating } = useReactivateSubscription({
    onCompleted: () => {
      refetchBillingData();
    },
    onError: (error) => {
      console.error("Failed to reactivate subscription:", error);
    },
  });

  const displayName = summoner?.riotUserName || "User";
  const iconId = summoner?.iconId ? Number(summoner.iconId) : 1;
  const avatarUrl = getProfileIconImg(iconId);
  const renewDate = subscription?.currentPeriodEnd ? moment(subscription.currentPeriodEnd * 1000).format("MMM. DD, YYYY") : null;

  // Hook for setting default payment method
  const { setDefaultPaymentMethod } = useSetDefaultPaymentMethod({
    onCompleted: () => {},
    onError: (error) => {
      console.error("Failed to set default payment method:", error);
    },
  });

  // Hook for deleting payment method
  const { deletePaymentMethod, isLoading: isDeletingPaymentMethod } = useDeletePaymentMethod({
    onCompleted: () => {
      setShowDeletePaymentModal(false);
      setSelectedPaymentMethod(null);
      refetchBillingData();
    },
    onError: (error) => {
      console.error("Failed to delete payment method:", error);
    },
  });

  // Build payment method options for dropdown
  const paymentMethodOptions = useMemo(() => {
    return billingData.paymentMethods.map(
      (method) =>
        ({
          value: method.id,
          label: method.type === "paypal" ? "PayPal" : `Card ending in ${method.lastFour}`,
          icon: method.type === "paypal" ? "5" : getCardIcon(method.cardType || ""),
          type: method.type === "paypal" ? "paypal" : "card",
        } as PaymentMethodOption),
    );
  }, [billingData.paymentMethods]);

  // Set default selected payment method (find the one marked as default, or first one)
  useEffect(() => {
    // Check if selected method still exists in options (handles deletion case)
    const selectedStillExists =
      selectedPaymentMethod && paymentMethodOptions.some((opt) => opt.value === selectedPaymentMethod.value);

    if ((!selectedPaymentMethod || !selectedStillExists) && paymentMethodOptions.length > 0) {
      // Try to find the default payment method
      const defaultMethod = billingData.paymentMethods.find((method) => method.isDefault);
      if (defaultMethod) {
        const defaultOption = paymentMethodOptions.find((opt) => opt.value === defaultMethod.id);
        if (defaultOption) {
          setSelectedPaymentMethod(defaultOption);
          return;
        }
      }
      // Otherwise use first option
      setSelectedPaymentMethod(paymentMethodOptions[0]);
    } else if (paymentMethodOptions.length === 0) {
      setSelectedPaymentMethod(null);
    }
  }, [paymentMethodOptions, selectedPaymentMethod, billingData.paymentMethods]);

  const handlePaymentMethodChange = (option: PaymentMethodOption) => {
    setSelectedPaymentMethod(option);
    // Call API to set this as the default payment method
    setDefaultPaymentMethod(option.value);
  };

  if (userLoading || billingLoading || subscriptionLoading) {
    return (
      <div className="billing-overview flex items-center justify-center py-12">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Get billing info display (PayPal email or card info)
  const getBillingInfoDisplay = () => {
    const defaultMethod = billingData.paymentMethods.find((method) => method.isDefault);
    if (defaultMethod) {
      if (defaultMethod.type === "paypal") {
        return {
          icon: <Icon icon={<SvgMiscPaypal />} className="w-[15px] h-[15px]" />,
          label: defaultMethod.paypalEmail || "PayPal",
        };
      }
      return {
        icon: <Icon icon={<SvgMiscCreditCard />} className="w-[15px] h-[15px]" />,
        label: defaultMethod.lastFour ? `Card ending in ${defaultMethod.lastFour}` : "Card",
      };
    }
    return null;
  };

  const billingInfoDisplay = getBillingInfoDisplay();

  const billingMessage = getBillingMessage(
    paymentMethodOptions.length > 0,
    !!subscription?.isOnFreeTrial,
    renewDate,
    unitAmount,
    isCanceledButActive,
  );

  // Render the appropriate action button based on subscription state
  const renderActionButton = () => {
    // State 1: Non-premium user
    if (!hasActiveSubscription) {
      return (
        <Link
          to={AppRoutes.PLUS}
          className="flex items-center justify-center px-3 py-2.5 bg-accent-brown-400 hover:bg-purple-100 border border-accent-yellow-400 hover:opacity-90 text-white text-[13px] font-bold rounded-[3px] transition-opacity"
        >
          {annualPlanHasFreeTrial ? (
            <>
              Try&nbsp;<span className="text-accent-yellow-400">PLUS</span>&nbsp;Free
            </>
          ) : (
            <>
              Get&nbsp;<span className="text-accent-yellow-400">PLUS</span>
            </>
          )}
        </Link>
      );
    }

    // State 3: Canceled but still active (until period ends)
    if (isCanceledButActive) {
      const defaultPaymentMethod = billingData.paymentMethods.find((method) => method.isDefault);
      const handleReactivate = () => {
        // If no payment method, show error and prompt to add one
        if (!defaultPaymentMethod?.id) {
          setPaymentMethodError(true);
          // Clear error after 5 seconds
          setTimeout(() => setPaymentMethodError(false), 5000);
          return;
        }
        reactivateSubscription(defaultPaymentMethod.id);
      };

      return (
        <button
          onClick={handleReactivate}
          disabled={isReactivating}
          className="flex items-center justify-center px-3 py-2.5 bg-accent-blue-400 hover:bg-accent-blue-600 text-white text-[13px] font-bold rounded-[3px] transition-colors disabled:opacity-50"
        >
          {isReactivating ? "Reactivating..." : "Renew Subscription"}
        </button>
      );
    }

    // State 2: Active premium user
    if (isActivePremium) {
      return (
        <button
          onClick={() => setShowCancelModal(true)}
          className="flex items-center justify-center px-3 py-2.5 bg-lavender-500 hover:bg-purple-500 text-white text-[13px] font-bold rounded-[3px] transition-colors"
        >
          Cancel Subscription
        </button>
      );
    }

    return null;
  };

  return (
    <div className="billing-overview flex flex-col gap-y-4.5 mb-[36px]">
      {/* Profile Header Section */}
      <div className="flex items-center justify-between bg-purple-400 rounded-[3px] p-4">
        <div className="flex items-center gap-8">
          {/* Avatar and Username */}
          <div className="flex items-center gap-[12px]">
            <img src={avatarUrl} alt="User avatar" className="w-[48px] h-[48px] rounded-[3px]" />
            <div>
              <div
                className={classnames("text-[16px] font-bold", hasActiveSubscription ? "text-accent-yellow-400" : "text-white")}
              >
                {displayName}
              </div>
            </div>
          </div>

          {/* Plan Type */}
          <div>
            <div className="text-lavender-200 text-xs mb-2">Plan Type</div>
            <div
              className={classnames(
                "text-xs",
                hasActiveSubscription ? "text-accent-yellow-400 font-semibold font-medium" : "text-lavender-50",
              )}
            >
              {getPlanTypeLabel(subscription?.planInterval, hasActiveSubscription)}
            </div>
          </div>

          {/* Plan Renews / Plan Ends - only show for premium users */}
          {hasActiveSubscription && (
            <div>
              <div className="text-lavender-200 text-xs mb-2">{isCanceledButActive ? "Plan Ends" : "Plan Renews"}</div>
              <div className="text-lavender-50 text-xs font-medium">{renewDate || "-"}</div>
            </div>
          )}

          {/* Billing Information - only show for premium users */}
          {hasActiveSubscription && (
            <div>
              <div className="text-lavender-200 text-xs mb-2">Billing Information</div>
              <div className="flex items-center gap-1.5 text-lavender-50 text-xs font-medium">
                {billingInfoDisplay ? (
                  <>
                    {billingInfoDisplay.icon}
                    <span className="truncate max-w-[150px]">{billingInfoDisplay.label}</span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Button */}
        {renderActionButton()}
      </div>

      {/* Payment Section - only show if user has active subscription */}
      {hasActiveSubscription && (
        <div className="bg-purple-400 rounded-[3px]">
          <div className="p-4.5 bg-purple-200 rounded-t-[3px]">
            <HeaderBullet>Payment</HeaderBullet>
          </div>
          <div className="flex justify-between items-end">
            {/* Billing Information Text */}
            <div className="p-4.5">
              <div className="text-white text-sm font-bold mb-3">Billing Information</div>
              <div className="text-lavender-100 text-sm py-2">
                {billingMessage.prefix}
                {billingMessage.date && <span className="text-white font-bold"> {billingMessage.date}</span>}
                {billingMessage.middle}
                {billingMessage.amount && <span className="text-white font-bold"> {billingMessage.amount}</span>}
              </div>
            </div>

            {/* Payment Method Dropdown */}
            <div className="min-w-[200px] border-l border-l-purple-500 p-4.5 relative">
              <div className="flex justify-between items-center mb-3">
                <div className="text-white text-xs font-bold">Payment Method</div>
                {paymentMethodOptions.length > 0 && (
                  <button
                    onClick={() => setShowDeletePaymentModal(true)}
                    className="text-accent-blue-400 text-xs font-bold underline"
                  >
                    Delete
                  </button>
                )}
              </div>
              <Dropdown<PaymentMethodOption>
                value={selectedPaymentMethod || undefined}
                onChange={handlePaymentMethodChange}
                className="w-full"
              >
                <DropdownButton
                  className={classnames(
                    "flex items-center gap-[8px] rounded-[3px] px-3 py-2 justify-between w-full h-auto transition-all",
                    paymentMethodError ? "bg-accent-red-400/20 ring-2 ring-accent-red-400" : "bg-purple-100",
                  )}
                >
                  {({ open }) => (
                    <>
                      {selectedPaymentMethod ? (
                        <>
                          {selectedPaymentMethod.type === "paypal" ? (
                            <Icon icon={<SvgMiscPaypal />} className="w-5 h-3.5" />
                          ) : (
                            <Icon icon={<SvgMiscCreditCard />} className="w-5 h-3.5" />
                          )}
                          <span className="text-white text-sm flex-1 text-left">{selectedPaymentMethod.label}</span>
                        </>
                      ) : (
                        <span className="text-lavender-100 text-sm">Select payment method</span>
                      )}
                      <SvgArrowFillDown
                        className={classnames("w-3 h-3 text-white transition-transform", {
                          "rotate-180": open,
                        })}
                      />
                    </>
                  )}
                </DropdownButton>
                <DropdownOptions className="bg-purple-100 rounded-[3px] min-w-[200px]">
                  {/* Section header */}
                  <div className="px-3 py-1.5 text-lavender-50 text-xxs font-semibold ">Payment Methods</div>
                  {paymentMethodOptions.map((option) => (
                    <DropdownOption
                      key={option.value}
                      value={option}
                      className="flex items-center gap-2.5 px-3 py-2 dark:bg-purple-100 hover:bg-lavender-500 cursor-pointer rounded-[3px]"
                    >
                      {option.type === "paypal" ? (
                        <Icon icon={<SvgMiscPaypal />} className="w-3.5 h-3.5" />
                      ) : (
                        <Icon icon={<SvgMiscCreditCard />} className="w-3.5 h-3.5" />
                      )}
                      <span className="text-white text-sm">{option.label}</span>
                    </DropdownOption>
                  ))}
                  {/* Add new method button */}
                  <div
                    className="flex items-center px-3 py-2 cursor-pointer hover:bg-lavender-500"
                    onClick={() => setShowAddPaymentModal(true)}
                  >
                    <span className="text-lavender-50 text-sm">
                      <span className="inline-flex items-center justify-center w-[14px] h-[14px] mr-2">+</span>
                      Change Payment Method
                    </span>
                  </div>
                </DropdownOptions>
              </Dropdown>
              {paymentMethodError && (
                <div className="text-accent-red-400 text-xs mt-2">Please add a payment method to renew.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment History Section - only show if there are invoices */}
      {invoices && invoices.length > 0 && (
        <div className="bg-purple-400 rounded-[3px]">
          <div className="p-4.5 bg-purple-200 rounded-t-[3px]">
            <HeaderBullet>Payment History</HeaderBullet>
          </div>
          <PaymentHistoryTableContent
            invoices={invoices}
            planInterval={subscription?.planInterval}
            defaultPaymentType={billingData.paymentMethods.find((m) => m.isDefault)?.type === "paypal" ? "paypal" : "card"}
          />
        </div>
      )}

      {/* Add Payment Method Modal */}
      {showAddPaymentModal && <AddPaymentMethodModal onClose={() => setShowAddPaymentModal(false)} />}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <CancelSubscriptionModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={cancelSubscription}
          onOpenContactForm={() => setShowFeedbackForm(true)}
          isLoading={isCanceling}
          renewDate={renewDate}
        />
      )}

      {/* Feedback Form */}
      {showFeedbackForm && <FeedbackForm toggleFeedbackPopup={() => setShowFeedbackForm(false)} />}

      {/* Delete Payment Method Modal */}
      {showDeletePaymentModal && (
        <DeletePaymentMethodModal
          onClose={() => setShowDeletePaymentModal(false)}
          onConfirm={deletePaymentMethod}
          isLoading={isDeletingPaymentMethod}
          paymentMethod={selectedPaymentMethod}
        />
      )}
    </div>
  );
};

interface PaymentHistoryTableProps {
  invoices: Invoice[];
  planInterval?: string | null;
  defaultPaymentType?: "card" | "paypal" | null;
}

const formatCurrency = (amount: number, currency: string | null | undefined): string => {
  const currencyCode = currency?.toUpperCase() || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(amount / 100);
  } catch {
    return `${currencyCode} ${(amount / 100).toFixed(2)}`;
  }
};

const PaymentHistoryTableContent: React.FC<PaymentHistoryTableProps> = ({ invoices, planInterval, defaultPaymentType }) => {
  const data = useMemo(
    () =>
      invoices.map((payment) => ({
        date: payment.periodStart,
        description: getPlanTypeLabel(planInterval, true),
        paymentMethod: {
          brand: payment.brand,
          last4: payment.last4,
        },
        amount: {
          value: payment.totalPaid,
          currency: payment.currency,
        },
      })),
    [invoices, planInterval],
  );

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date" as const,
        Cell: ({ value }: { value: number | null | undefined }) => (
          <div className="text-white">{value ? moment(value * 1000).format("MMMM DD, YYYY") : "-"}</div>
        ),
      },
      {
        Header: "Description",
        accessor: "description" as const,
        Cell: ({ value }: { value: React.ReactNode }) => <>{value}</>,
      },
      {
        Header: "Payment Method",
        accessor: "paymentMethod" as const,
        Cell: ({ value }: { value: { brand: string | null | undefined; last4: string | null | undefined } }) => {
          const isPayPal =
            value.brand?.toLowerCase() === "paypal" || (!value.brand && !value.last4 && defaultPaymentType === "paypal");
          if (isPayPal) {
            return <div className="text-white">PayPal</div>;
          }
          return (
            <div className="text-white">
              {!value.brand && !value.last4 ? "-" : `${value.brand || "Card"} ••••${value.last4 || ""}`}
            </div>
          );
        },
      },
      {
        Header: "Amount",
        accessor: "amount" as const,
        Cell: ({ value }: { value: { value: number | null | undefined; currency: string | null | undefined } }) => {
          const amount = value.value;
          const isValidAmount = typeof amount === "number" && !isNaN(amount) && isFinite(amount);
          const displayAmount = isValidAmount ? formatCurrency(amount, value.currency) : "$0.00";
          return <div className="text-white">{displayAmount}</div>;
        },
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <div {...getTableProps()} className="w-full p-4.5">
      <div className="mb-4.5 pl-4.5 pr-4.5">
        {headerGroups.map((headerGroup, index) => (
          <div {...headerGroup.getHeaderGroupProps({ key: `header-group-${index}` })} className="flex">
            {headerGroup.headers.map((column, colIndex) => (
              <div
                {...column.getHeaderProps({ key: `header-${colIndex}` })}
                className={classnames("text-lavender-50 text-xs font-medium", {
                  "flex-1": colIndex < 3,
                  "w-[100px] text-right": colIndex === 3,
                })}
              >
                {column.render("Header")}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps()}>
        {rows.map((row, rowIndex) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps({ key: `row-${rowIndex}` })}
              className={classnames("flex py-[12px] px-4.5", {
                "bg-purple-200 rounded-[3px]": rowIndex % 2 === 0,
              })}
            >
              {row.cells.map((cell, cellIndex) => (
                <div
                  {...cell.getCellProps({ key: `cell-${rowIndex}-${cellIndex}` })}
                  className={classnames("text-sm", {
                    "flex-1": cellIndex < 3,
                    "w-[100px] text-right": cellIndex === 3,
                  })}
                >
                  {cell.render("Cell")}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getCardIcon = (brand: string): string => {
  const brandLower = brand?.toLowerCase() || "";
  if (brandLower.includes("visa")) return "1";
  if (brandLower.includes("mastercard") || brandLower.includes("master")) return "2";
  if (brandLower.includes("amex") || brandLower.includes("american")) return "3";
  if (brandLower.includes("discover")) return "4";
  return "1";
};

// ============================================
// Add Payment Method Modal
// ============================================
interface AddPaymentMethodModalProps {
  onClose: () => void;
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({ onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "inherit";
    };
  }, []);

  return (
    <div>
      {/* Overlay */}
      <div className="fixed z-modal-overlay inset-0 bg-purple-500 bg-opacity-75" onClick={onClose} />

      {/* Modal */}
      <div className="fixed z-modal-content top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] max-h-[90vh] overflow-y-auto rounded-[6px] bg-purple-200">
        <div className="p-[24px]">
          {/* Close button */}
          <IconX className="ml-auto mb-[12px] w-[14px] cursor-pointer [&_path]:fill-white" onClick={onClose} />

          {/* Checkout Form - wrapped in Recurly providers */}
          <RecurlyProvider publicKey={RECURLY_PUBLIC_KEY}>
            <Elements>
              <AddPaymentMethodForm onClose={onClose} />
            </Elements>
          </RecurlyProvider>
        </div>
      </div>
    </div>
  );
};

// Inner component that uses useRecurly (must be inside Elements)
interface AddPaymentMethodFormProps {
  onClose: () => void;
}

const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = ({ onClose }) => {
  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormState>({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [cardSaved, setCardSaved] = useState(false);
  const [paypalSaved, setPaypalSaved] = useState(false);
  const [pendingPaymentType, setPendingPaymentType] = useState<"card" | "paypal" | null>(null);

  // Get states for selected country (for validation)
  const states = formData.country ? State.getStatesOfCountry(formData.country) : [];

  // Form validation with debounce
  const { fieldErrors, handleBlur, handleFieldChange, validateAllOnSubmit } = useFormValidation({ formData, states });

  const {
    addPaymentMethod,
    isLoading: isAddingPayment,
    error: addPaymentError,
  } = useAddPaymentMethod({
    onCompleted: () => {
      if (pendingPaymentType === "paypal") {
        setPaypalSaved(true);
      } else {
        setCardSaved(true);
      }
      setPendingPaymentType(null);
      // Close modal and refresh page after short delay
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setFormErrors([error]);
      setPendingPaymentType(null);
    },
  });

  const isLoading = isTokenizing || isAddingPayment;
  const errors = [...new Set([...formErrors, addPaymentError].filter(Boolean))] as string[];

  const handleSaveCard = () => {
    if (!validateAllOnSubmit()) {
      return;
    }
    setFormErrors([]);
    setIsTokenizing(true);
    setPendingPaymentType("card");

    recurly.token(formRef.current!, (err, token) => {
      if (err) {
        setIsTokenizing(false);
        setPendingPaymentType(null);
        const errorMessages = err.details?.map((d) => d.messages.join(", ")).join(", ") || err.message;
        setFormErrors([errorMessages]);
        return;
      }

      setIsTokenizing(false);
      addPaymentMethod(token.id);
    });
  };

  const handlePayPalToken = useCallback(
    (token: { id: string }) => {
      setFormErrors([]);
      setPendingPaymentType("paypal");
      addPaymentMethod(token.id);
    },
    [addPaymentMethod],
  );

  return (
    <CheckoutForm
      formRef={formRef}
      formData={formData}
      setFormData={setFormData}
      onSaveCard={handleSaveCard}
      onPayPalToken={handlePayPalToken}
      isLoading={isLoading}
      errors={errors}
      cardSaved={cardSaved}
      paypalSaved={paypalSaved}
      fieldErrors={fieldErrors}
      onFieldBlur={handleBlur}
      onFieldChange={handleFieldChange}
    />
  );
};

export default BillingOverview;
